import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { ChevronDown, Check, X } from 'lucide-react';
import { theme } from '../../tokens';

const selectSizes = {
  sm: css`
    height: 36px;
    padding: 0 ${theme.spacing[3]};
    font-size: ${theme.fontSizes.sm};
  `,
  md: css`
    height: 44px;
    padding: 0 ${theme.spacing[4]};
    font-size: ${theme.fontSizes.base};
  `,
  lg: css`
    height: 52px;
    padding: 0 ${theme.spacing[5]};
    font-size: ${theme.fontSizes.lg};
  `,
};

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1.5]};
  width: 100%;
  position: relative;
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};

  ${({ $required }) =>
    $required &&
    css`
      &::after {
        content: ' *';
        color: ${theme.colors.error[500]};
      }
    `}
`;

const SelectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: 1px solid ${theme.colors.border.default};
  border-radius: ${theme.radii.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-family: ${theme.fontFamilies.sans};
  text-align: left;
  cursor: pointer;
  transition: ${theme.transitions.input};
  outline: none;

  ${({ $size }) => selectSizes[$size] || selectSizes.md}

  &:hover:not(:disabled) {
    border-color: ${theme.colors.border.dark};
  }

  &:focus {
    border-color: ${theme.colors.border.focus};
    box-shadow: ${theme.shadows.focus};
  }

  &:disabled {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.disabled};
    cursor: not-allowed;
  }

  ${({ $error }) =>
    $error &&
    css`
      border-color: ${theme.colors.error[500]};

      &:focus {
        border-color: ${theme.colors.error[500]};
        box-shadow: ${theme.shadows.focusError};
      }
    `}

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      border-color: ${theme.colors.border.focus};
      box-shadow: ${theme.shadows.focus};
    `}
`;

const SelectValue = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $placeholder }) =>
    $placeholder &&
    css`
      color: ${theme.colors.text.tertiary};
    `}
`;

const ChevronIcon = styled(ChevronDown)`
  width: 20px;
  height: 20px;
  color: ${theme.colors.text.tertiary};
  transition: transform ${theme.durations.fast} ${theme.easings.easeOut};
  flex-shrink: 0;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: ${theme.spacing[1]};
  background: ${theme.colors.background.elevated};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.radii.lg};
  box-shadow: ${theme.shadows.lg};
  z-index: ${theme.zIndices.dropdown};
  max-height: 280px;
  overflow-y: auto;
  padding: ${theme.spacing[1]};

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.neutral[300]};
    border-radius: ${theme.radii.full};
  }
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[2.5]} ${theme.spacing[3]};
  border-radius: ${theme.radii.md};
  cursor: pointer;
  transition: background ${theme.durations.fast} ${theme.easings.ease};

  &:hover {
    background: ${theme.colors.background.secondary};
  }

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      background: ${theme.colors.primary[50]};
      color: ${theme.colors.primary[600]};

      &:hover {
        background: ${theme.colors.primary[100]};
      }
    `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

const OptionLabel = styled.span`
  flex: 1;
`;

const CheckIcon = styled(Check)`
  width: 18px;
  height: 18px;
  color: ${theme.colors.primary[500]};
`;

const HelperText = styled.span`
  font-size: ${theme.fontSizes.xs};
  color: ${({ $error }) => ($error ? theme.colors.error[500] : theme.colors.text.secondary)};
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[1]};
  margin-right: ${theme.spacing[1]};
  border: none;
  background: transparent;
  color: ${theme.colors.text.tertiary};
  cursor: pointer;
  border-radius: ${theme.radii.sm};

  &:hover {
    background: ${theme.colors.neutral[100]};
    color: ${theme.colors.text.secondary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Select = React.forwardRef(
  (
    {
      label,
      options = [],
      value,
      onChange,
      placeholder = 'Select an option',
      helperText,
      error,
      size = 'md',
      required,
      disabled,
      clearable,
      className,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    const selectedOption = options.find((opt) => opt.value === value);

    const handleSelect = (option) => {
      if (option.disabled) return;
      onChange?.(option.value);
      setIsOpen(false);
    };

    const handleClear = (e) => {
      e.stopPropagation();
      onChange?.(null);
    };

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      },
      []
    );

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    return (
      <SelectWrapper ref={wrapperRef} className={className} {...props}>
        {label && <Label $required={required}>{label}</Label>}
        <SelectButton
          ref={ref}
          type="button"
          $size={size}
          $error={error}
          $isOpen={isOpen}
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <SelectValue $placeholder={!selectedOption}>
            {selectedOption ? selectedOption.label : placeholder}
          </SelectValue>
          {clearable && value && (
            <ClearButton type="button" onClick={handleClear} aria-label="Clear selection">
              <X />
            </ClearButton>
          )}
          <ChevronIcon $isOpen={isOpen} />
        </SelectButton>

        {isOpen && (
          <Dropdown role="listbox">
            {options.map((option) => (
              <Option
                key={option.value}
                role="option"
                $isSelected={option.value === value}
                $isDisabled={option.disabled}
                aria-selected={option.value === value}
                onClick={() => handleSelect(option)}
              >
                <OptionLabel>{option.label}</OptionLabel>
                {option.value === value && <CheckIcon />}
              </Option>
            ))}
          </Dropdown>
        )}

        {helperText && <HelperText $error={error}>{helperText}</HelperText>}
      </SelectWrapper>
    );
  }
);

Select.displayName = 'Select';

export default Select;
